import { useState } from "react";
import { Copy, X } from "lucide-react";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { useTheme } from '../../context/ThemeContext';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCode } from "@fortawesome/free-solid-svg-icons";

export default function Base64Encode() {
    const [input, setInput] = useState("");
useTheme();
    const [encoded, setEncoded] = useState("");
    const [toast, setToast] = useState({ message: "", visible: false });

    const showToast = (message) => {
        setToast({ message, visible: true });
        setTimeout(() => {
            setToast({ message: "", visible: false });
        }, 3000);
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInput(value);
        if (value) {
            try {
                setEncoded(btoa(value));
            } catch (e) {
                setEncoded("Error: Invalid input for Base64 encoding");
            }
        } else {
            setEncoded("");
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        showToast("Copied to clipboard");
    };

    const clearAll = () => {
        setInput("");
        setEncoded("");
    };

    return (
        <>
            <HeaderSection toolId="base64Encoder"/>
            <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
                <div className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon icon={faCode} className="mr-3" />
                            Base64 Encoder
                        </h1>
                        <p className="opacity-90">
                            Encode text to Base64 format for safe data transmission
                        </p>
                    </div>

                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center mb-3">
                            <label className="block text-gray-700 dark:text-gray-200 font-medium">
                                Text Input
                            </label>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => copyToClipboard(input)}
                                    className="text-xs px-3 py-1 bg-blue-50 dark:bg-gray-600 text-blue-600 dark:text-gray-300 rounded-full hover:bg-blue-100 dark:hover:bg-gray-500 transition flex items-center"
                                >
                                    <Copy size={12} className="mr-1" /> Copy
                                </button>
                                <button
                                    onClick={clearAll}
                                    className="text-xs px-3 py-1 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-500 transition flex items-center"
                                >
                                    <X size={12} className="mr-1" /> Clear
                                </button>
                            </div>
                        </div>
                        <textarea
                            value={input}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            placeholder="Enter text to encode..."
                            rows={6}
                            spellCheck="false"
                        />
                    </div>

                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center mb-3">
                            <label className="block text-gray-700 dark:text-gray-200 font-medium">
                                Base64 Encoded Output
                            </label>
                            <button
                                onClick={() => copyToClipboard(encoded)}
                                className="text-xs px-3 py-1 bg-green-50 dark:bg-gray-600 text-green-600 dark:text-gray-300 rounded-full hover:bg-green-100 dark:hover:bg-gray-500 transition flex items-center"
                            >
                                <Copy size={12} className="mr-1" /> Copy
                            </button>
                        </div>
                        <textarea
                            value={encoded}
                            readOnly
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white font-mono"
                            placeholder="Base64 encoded result will appear here..."
                            rows={6}
                        />
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 p-6">
                        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                            About Base64 Encoding
                        </h2>
                        <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                            Base64 is a binary-to-text encoding scheme that represents binary data in an ASCII string format.
                            It's commonly used for encoding data in URLs, email attachments, and other text-based protocols.
                        </p>
                        
                        <div className="font-mono bg-gray-100 dark:bg-gray-600 rounded-lg p-4 overflow-x-auto">
                            <pre className="text-sm text-gray-800 dark:text-gray-100">
                                <span className="text-gray-500 dark:text-gray-300">/ / Try this text:</span>
                                {"\n"}Hello World!
                                {"\n\n"}
                                <span className="text-gray-500 dark:text-gray-300">/ /Will encode to:</span>
                                {"\n"}SGVsbG8gV29ybGQh
                            </pre>
                        </div>
                    </div>
                </div>
            </div>

            {toast.visible && (
                <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg">
                    {toast.message}
                </div>
            )}

            <BottomSection toolId="base64Encoder"/>
        </>
    );
}